import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { hasCapability, CAP } from '../utils/permissions';
import projectsService from '../services/projectsService';
import opportunitiesService from '../services/opportunitiesService';
import { extractClientFromText } from '../services/aiChatService';
import { usePlainTextClientDrop } from '../hooks/usePlainTextClientDrop';
import { useFormFieldErrors } from '../hooks/useFormFieldErrors';
import OpportunitiesInlineTable from '../components/OpportunitiesInlineTable';
import OpportunityFormFields from '../components/OpportunityFormFields';
import FormValidationAlert from '../components/FormValidationAlert';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import {
  Client,
  Office,
  Opportunity,
  OpportunityPipelineStatus,
  User,
} from '../types';
import {
  OPPORTUNITY_STATUS_DESCRIPTIONS,
  OPPORTUNITY_STATUS_LABELS,
  getActiveSalesUsers,
  getDefaultProbability,
  getStagesUpToStatus,
} from '../utils/commercialPlanning';
import {
  ContactAutocompleteOption,
  isCreateLeadOption,
} from '../utils/contactPicker';
import { opportunityToCreateProjectState } from '../utils/leadProjectCreate';
import {
  EMPTY_OPPORTUNITY_FORM,
  OpportunityFormValues,
  findExistingClientByName,
  formFromLostLead,
  formValuesToInput,
  isValidEmail,
  opportunityToFormValues,
  validateOpportunityForm,
} from '../utils/opportunityForm';

const OpportunitiesPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isDark = theme.palette.mode === 'dark';
  const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';
  const surfaceBg = isDark ? '#0f2134' : '#ffffff';

  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<OpportunityFormValues>(EMPTY_OPPORTUNITY_FORM);
  const [companyInputValue, setCompanyInputValue] = useState('');
  const [creatingContact, setCreatingContact] = useState(false);
  const [notesFullscreen, setNotesFullscreen] = useState(false);
  const fieldErrors = useFormFieldErrors();

  const loadOpportunities = useCallback(async () => {
    const data = await opportunitiesService.listOpportunities();
    setOpportunities(data);
  }, []);

  const handleDroppedPlainText = useCallback(
    async (text: string) => {
      const dialogWasOpen = dialogOpen;
      setError(null);
      let createClientFailed = false;
      try {
        const data = await extractClientFromText(text);
        const company = (data.name || '').trim();
        const mainEmail = (data.main_contact_email || '').trim();

        let clientId: number | '' = '';
        if (company) {
          const existing = findExistingClientByName(clients, company);
          if (existing) {
            clientId = existing.id;
          } else {
            const pocsPayload = (data.pocs || [])
              .filter((p) => (p.full_name || '').trim())
              .map((p) => ({
                full_name: p.full_name.trim(),
                phone: (p.phone || '').trim() || undefined,
                job_position: (p.job_position || '').trim() || undefined,
              }));
            try {
              const created = await projectsService.createClient({
                name: company,
                kind: 'lead',
                abbreviation:
                  data.abbreviation && data.abbreviation.length === 2 ? data.abbreviation : undefined,
                pocs: pocsPayload,
              });
              const refreshed = await projectsService.getClients();
              setClients(refreshed);
              const row = refreshed.find((c) => c.id === created.client_id);
              clientId = row?.id ?? created.client_id;
            } catch (createErr: unknown) {
              createClientFailed = true;
              const msg =
                createErr && typeof createErr === 'object' && 'response' in createErr
                  ? (createErr as { response?: { data?: { error?: string } } }).response?.data?.error
                  : null;
              setError(
                msg ||
                  (createErr instanceof Error ? createErr.message : 'Could not create lead; fields prefilled.'),
              );
            }
          }
        }

        setForm((prev) => {
          const base = dialogWasOpen ? prev : { ...EMPTY_OPPORTUNITY_FORM };
          return {
            ...base,
            ...(clientId !== '' ? { client_id: clientId } : {}),
            ...(mainEmail && isValidEmail(mainEmail) ? { email: mainEmail.trim() } : {}),
            ...((!base.title || !String(base.title).trim()) && company ? { title: company } : {}),
          };
        });

        if (company) {
          setCompanyInputValue(company);
        }

        if (!dialogWasOpen) {
          setDialogOpen(true);
        }

        if (!company) {
          setError('Could not detect a company name in the text.');
          return;
        }

        if (clientId !== '') {
          setSuccess('Client matched or created. Review the opportunity and save.');
          setTimeout(() => setSuccess(null), 6000);
        } else if (createClientFailed && mainEmail) {
          setSuccess('Email prefilled when found; fix the client error above, then save.');
          setTimeout(() => setSuccess(null), 6000);
        }
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Failed to extract client data';
        setError(msg);
      }
    },
    [clients, dialogOpen],
  );

  const { busy: extractBusy, rootDropProps, dropHighlightSx } = usePlainTextClientDrop({
    enabled: !dialogOpen,
    onTextDropped: handleDroppedPlainText,
    onFileOnlyDrop: () => setError('Only plain text can be dropped here, not files.'),
  });

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [clientData, userData, officeData, opportunityData] = await Promise.all([
          projectsService.getClients(),
          projectsService.getUsers(),
          projectsService.getOffices(),
          opportunitiesService.listOpportunities(),
        ]);
        setClients(clientData);
        setUsers(userData);
        setOffices(officeData);
        setOpportunities(opportunityData);
        setError(null);
      } catch (err) {
        console.error(err);
        setError('Failed to load commercial data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const salesUsers = useMemo(
    () => getActiveSalesUsers(users),
    [users],
  );

  const canCreateProject = hasCapability(user, CAP.CREATE_PROJECT);

  const visibleStages = useMemo(
    () => getStagesUpToStatus(form.status),
    [form.status],
  );

  const resetDialog = () => {
    setForm(EMPTY_OPPORTUNITY_FORM);
    setCompanyInputValue('');
    setNotesFullscreen(false);
    setDialogOpen(false);
    fieldErrors.clear();
  };

  const handleLeadDialogClose = () => {
    if (notesFullscreen) {
      setNotesFullscreen(false);
      return;
    }
    resetDialog();
  };

  const handleClientChange = (clientId: number | '') => {
    setForm((prev) => ({
      ...prev,
      client_id: clientId,
      email: '',
    }));
    if (clientId === '') {
      setCompanyInputValue('');
    } else {
      const client = clients.find((item) => item.id === clientId);
      setCompanyInputValue(client?.name || '');
    }
  };

  const handleCompanySelection = async (option: ContactAutocompleteOption | null) => {
    if (!option) {
      handleClientChange('');
      return;
    }

    if (isCreateLeadOption(option)) {
      setCreatingContact(true);
      setError(null);
      try {
        const created = await projectsService.createClient({
          name: option.name.trim(),
          kind: 'lead',
        });
        const refreshed = await projectsService.getClients();
        setClients(refreshed);
        const createdClient =
          created.client ||
          refreshed.find((item) => item.id === created.client_id) ||
          null;
        if (createdClient) {
          handleClientChange(createdClient.id);
          setCompanyInputValue(createdClient.name);
        }
      } catch (err: unknown) {
        const msg =
          err && typeof err === 'object' && 'response' in err
            ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
            : null;
        fieldErrors.show(msg || (err instanceof Error ? err.message : 'Failed to create lead'));
      } finally {
        setCreatingContact(false);
      }
      return;
    }

    handleClientChange(option.id);
    setCompanyInputValue(option.name);
  };

  const handleStatusChange = (status: OpportunityPipelineStatus) => {
    setForm((prev) => ({
      ...prev,
      status,
      probability_to_win: String(getDefaultProbability(status)),
    }));
  };

  const handleEdit = (opportunity: Opportunity) => {
    setForm(opportunityToFormValues(opportunity));
    const linkedClient = opportunity.client_id
      ? clients.find((client) => client.id === opportunity.client_id)
      : null;
    setCompanyInputValue(linkedClient?.name || opportunity.client_name || '');
    fieldErrors.clear();
    setDialogOpen(true);
  };

  const handleCreateLeadFromLost = (opportunity: Opportunity) => {
    setForm(formFromLostLead(opportunity));
    const linkedClient = opportunity.client_id
      ? clients.find((client) => client.id === opportunity.client_id)
      : null;
    setCompanyInputValue(linkedClient?.name || opportunity.client_name || '');
    setNotesFullscreen(false);
    fieldErrors.clear();
    setDialogOpen(true);
  };

  const handleCreateProposal = (opportunity: Opportunity) => {
    navigate('/projects/create', {
      state: opportunityToCreateProjectState(opportunity, 'proposal'),
    });
  };

  const handleCreateProject = (opportunity: Opportunity) => {
    navigate('/projects/create', {
      state: opportunityToCreateProjectState(opportunity, 'awarded'),
    });
  };

  const handleOpenDocuments = (projectId: number) => {
    navigate(`/projects/${projectId}/documents`);
  };

  const handleSave = async () => {
    const validationError = validateOpportunityForm(form);
    if (validationError) {
      fieldErrors.show(validationError.message, validationError.fields);
      return;
    }

    setSaving(true);
    fieldErrors.clear();
    try {
      const payload = formValuesToInput(form, { notesAuthor: user?.name });
      if (form.id) {
        await opportunitiesService.updateOpportunity(form.id, payload);
        setSuccess('Opportunity updated');
      } else {
        await opportunitiesService.createOpportunity(payload);
        setSuccess('Opportunity created');
      }
      await loadOpportunities();
      resetDialog();
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : null;
      fieldErrors.show(msg || (err instanceof Error ? err.message : 'Failed to save opportunity'));
    } finally {
      setSaving(false);
    }
  };

  const handleNextStepSave = async (
    opportunity: Opportunity,
    targetStatus: OpportunityPipelineStatus,
    nextForm: OpportunityFormValues,
  ) => {
    const validationError = validateOpportunityForm(nextForm);
    if (validationError) {
      onTableError(validationError.message);
      return;
    }

    try {
      const updated = await opportunitiesService.updateOpportunity(
        opportunity.id,
        formValuesToInput(nextForm, { notesAuthor: user?.name }),
      );
      setOpportunities((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      setSuccess(`Moved to ${OPPORTUNITY_STATUS_LABELS[targetStatus]}`);
      setTimeout(() => setSuccess(null), 4000);
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : null;
      onTableError(msg || (err instanceof Error ? err.message : 'Failed to advance opportunity'));
    }
  };

  const onTableError = (message: string) => setError(message);

  const pageFillSx = {
    height: `calc(100vh - ${theme.spacing(10)} - ${theme.spacing(4)})`,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
    boxSizing: 'border-box',
  } as const;

  return (
    <Box
      {...rootDropProps}
      sx={{
        ...pageFillSx,
        position: 'relative',
        borderRadius: 1,
        transition: 'background-color 0.15s ease, outline 0.15s ease',
        ...dropHighlightSx,
      }}
    >
      {(extractBusy || loading) && (
        <LinearProgress
          sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: (t) => t.zIndex.modal + 2 }}
        />
      )}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        gap={2}
        flexWrap="wrap"
        sx={{ flexShrink: 0 }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, letterSpacing: '-0.01em' }}>
            Leads
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track commercial work before it becomes a live project. Use Next step to advance the pipeline,
            or the pencil to edit any field. Drop plain text on this page to create or match a client.
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<Add />}
          onClick={() => {
            setForm(EMPTY_OPPORTUNITY_FORM);
            setCompanyInputValue('');
            setNotesFullscreen(false);
            fieldErrors.clear();
            setDialogOpen(true);
          }}
        >
        Create Lead
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2, flexShrink: 0 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card
        sx={{
          border: `1px solid ${panelBorder}`,
          bgcolor: surfaceBg,
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <CardContent
          sx={{
            p: 0,
            '&:last-child': { pb: 0 },
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box px={2} py={1.5} borderBottom={`1px solid ${panelBorder}`} sx={{ flexShrink: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Commercial Pipeline
            </Typography>
          </Box>
          <OpportunitiesInlineTable
            opportunities={opportunities}
            clients={clients}
            salesUsers={salesUsers}
            offices={offices}
            canCreateProject={canCreateProject}
            onEdit={handleEdit}
            onUpdated={(updated) => {
              setOpportunities((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
            }}
            onDeleted={(id) => {
              setOpportunities((prev) => prev.filter((item) => item.id !== id));
            }}
            onError={onTableError}
            onCreateProposal={handleCreateProposal}
            onCreateProject={handleCreateProject}
            onCreateLeadFromLost={handleCreateLeadFromLost}
            onOpenDocuments={handleOpenDocuments}
            onNextStepSave={handleNextStepSave}
            findExistingClientByName={findExistingClientByName}
          />
        </CardContent>
      </Card>

      <Dialog
        open={dialogOpen}
        onClose={handleLeadDialogClose}
        maxWidth="md"
        fullWidth
        fullScreen={notesFullscreen}
        PaperProps={notesFullscreen ? { sx: { display: 'flex', flexDirection: 'column' } } : undefined}
      >
        <DialogTitle>{form.id ? 'Edit Lead' : 'Create Lead'}</DialogTitle>
        <FormValidationAlert message={fieldErrors.message} onClose={fieldErrors.clear} />
        <DialogContent
          sx={notesFullscreen ? {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden',
          } : undefined}
        >
          {!notesFullscreen && (
            <Grid container spacing={2} sx={{ pt: 1 }}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={form.status}
                    onChange={(e) => handleStatusChange(e.target.value as OpportunityPipelineStatus)}
                  >
                    {Object.entries(OPPORTUNITY_STATUS_LABELS).map(([value, label]) => {
                      const description =
                        OPPORTUNITY_STATUS_DESCRIPTIONS[value as OpportunityPipelineStatus];
                      return (
                        <MenuItem key={value} value={value}>
                          {description ? (
                            <Tooltip title={description} placement="right" enterDelay={300}>
                              <Box component="span" sx={{ display: 'block', width: '100%' }}>
                                {label}
                              </Box>
                            </Tooltip>
                          ) : (
                            label
                          )}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          )}
          <Box sx={{ pt: notesFullscreen ? 0 : 2, flex: notesFullscreen ? 1 : undefined, minHeight: 0, display: notesFullscreen ? 'flex' : undefined, flexDirection: notesFullscreen ? 'column' : undefined }}>
            <OpportunityFormFields
              form={form}
              setForm={setForm}
              visibleStages={visibleStages}
              clients={clients}
              salesUsers={salesUsers}
              offices={offices}
              companyInputValue={companyInputValue}
              setCompanyInputValue={setCompanyInputValue}
              onCompanySelection={handleCompanySelection}
              findExistingClientByName={findExistingClientByName}
              creatingContact={creatingContact}
              showProbability
              notesFullscreen={notesFullscreen}
              onToggleNotesFullscreen={() => setNotesFullscreen((prev) => !prev)}
              invalid={fieldErrors.invalid}
              onFieldEdit={fieldErrors.clearField}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetDialog} disabled={saving}>Cancel</Button>
          <Button variant="contained" color="secondary" onClick={handleSave} disabled={extractBusy || saving}>
            {saving ? 'Saving...' : 'Save Lead'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OpportunitiesPage;
